import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '../../../store'
import { PhoneCollectionModal } from './index'
import { userServices } from '../../../services'
import {
  hidePhoneCollectionModal,
  resetPhoneCollection,
  setPhonePreference,
  showPhoneCollectionModal,
} from '../../../global-state/phone-collection-slice'
import { updateConfiguration } from '../../../global-state/investor-slice'
import { showBanner } from '../../../global-state/banner-slice'

// Type for the phone preference values
type PhonePreference = 'saved' | 'skipped' | 'not-interested'

const GlobalPhoneCollectionModal: React.FC = () => {
  const dispatch = useDispatch()
  const { authToken } = useSelector((state: RootState) => state.investor)
  const { showPhoneModal } = useSelector((state: RootState) => state.phoneCollection)

  // Helper function to handle error banners
  const showErrorBanner = (message = 'Something went wrong! Please try again.') => {
    dispatch(
      showBanner({
        text: message,
        variant: 'error-banner',
      }),
    )
  }

  // Helper function to update phone preference in Redux and API
  const updatePhonePreference = async (
    preference: PhonePreference,
    data = {},
    setVerificationError: (error: string) => void = () => {},
  ) => {
    try {
      const response = await userServices.userProfileUpdate_v1(
        {
          phonePreference: preference,
          ...data,
        },
        { Authorization: authToken || '' },
      )

      if (response && response.status === 200) {
        dispatch(setPhonePreference(preference))

        if (preference !== 'saved') {
          dispatch(hidePhoneCollectionModal())
        } else {
          dispatch(resetPhoneCollection())
        }

        return true
      } else {
        const errorMessage =
          (response.data && response.data.message) || 'Something went wrong! Please try again.'
        setVerificationError(errorMessage)
        return false
      }
    } catch (error) {
      if (preference !== 'saved') {
        // For skip and not-interested, we still update the UI even if API fails
        dispatch(setPhonePreference(preference))
        dispatch(hidePhoneCollectionModal())
      } else {
        showErrorBanner()
      }
      return false
    }
  }

  // Helper function to calculate if the skip date is too old
  const isSkipDateExpired = (skipDate: string): boolean => {
    if (!skipDate) return false

    const skipDateTime = new Date(skipDate).getTime()
    const currentTime = new Date().getTime()
    const millisecondsInDay = 1000 * 60 * 60 * 24
    const daysDifference = (currentTime - skipDateTime) / millisecondsInDay

    // Show the modal after 15 days
    const requiredDays = 15
    return daysDifference >= requiredDays
  }

  // Generate OTP for phone verification
  const handleGenerateOTP = async (phone: string) => {
    try {
      const response = await userServices.generatePhoneOTP(phone, {
        Authorization: authToken || '',
      })

      if (response && response.status === 200) {
        return { success: true, message: response.data.message }
      } else {
        const errorMessage = (response.data && response.data.message) || 'Failed to send OTP'
        return { success: false, message: errorMessage }
      }
    } catch (error) {
      return { success: false, message: 'Failed to send OTP. Please try again.' }
    }
  }

  // Verify OTP
  const handleVerifyOTP = async (otp: string) => {
    try {
      const response = await userServices.verifyPhoneOTP(otp, { Authorization: authToken || '' })

      if (response && response.status === 200) {
        return { success: true, message: response.data.message }
      } else {
        const errorMessage = (response.data && response.data.message) || 'Invalid OTP'
        return { success: false, message: errorMessage }
      }
    } catch (error) {
      return { success: false, message: 'Invalid OTP. Please try again.' }
    }
  }

  const handlePhoneSubmit = async (values: {
    phone: string
    whatsappConsent: boolean
    setVerificationError: (error: string) => void
  }) => {
    // For users with WhatsApp consent, OTP verification is handled before this function is called
    // This function will only be directly called for users without WhatsApp consent
    const success = await updatePhonePreference(
      'saved',
      {
        phoneNumber: values.phone,
        whatsappConsent: values.whatsappConsent,
      },
      values.setVerificationError,
    )

    if (success) {
      // Update the investor state with the phone number
      dispatch(
        updateConfiguration({
          name: 'phone',
          value: values.phone,
        }),
      )

      dispatch(
        showBanner({
          text: 'Profile updated successfully',
          variant: 'success-banner',
          duration: 3000,
        }),
      )
    }
  }

  const handleSkip = async () => {
    await updatePhonePreference('skipped')
  }

  const handleNotInterested = async () => {
    await updatePhonePreference('not-interested')
  }

  useEffect(() => {
    const checkPhoneCollectionStatus = async () => {
      if (!authToken) return // Don't run if not authenticated

      try {
        const response = await userServices.getUserProfile_v1({
          Authorization: authToken || '',
        })

        if (response && response.status === 200 && response.data) {
          // Check if phone exists
          if (response.data.phoneNumber && response.data.phonePreference === 'saved') {
            dispatch(setPhonePreference('saved'))
            dispatch(hidePhoneCollectionModal())
            return response.data
          }

          // Check for phone_preference in the response
          const phonePreference = response.data.phonePreference

          if (phonePreference === 'not-interested') {
            // Never show the modal
            dispatch(setPhonePreference('not-interested'))
            dispatch(hidePhoneCollectionModal())
            return response.data
          }

          if (phonePreference === 'skipped') {
            dispatch(setPhonePreference('skipped'))

            const skipDate = response.data.phoneSkipDate
            if (isSkipDateExpired(skipDate)) {
              // It's been 15 or more days since skip, show the modal
              dispatch(showPhoneCollectionModal())
            } else {
              dispatch(hidePhoneCollectionModal())
            }
            return response.data
          }

          // Default case: If no preference is set or doesn't match any conditions,
          // show the modal to collect phone number
          dispatch(showPhoneCollectionModal())
        }
      } catch (error) {
        // Fail silently - don't show an error banner just because we couldn't check the phone status
        console.error('Error checking phone collection status:', error)
      }
    }

    checkPhoneCollectionStatus()
  }, [authToken, dispatch])

  return (
    <PhoneCollectionModal
      isOpen={showPhoneModal}
      onClose={() => dispatch(hidePhoneCollectionModal())}
      onSubmit={handlePhoneSubmit}
      onSkip={handleSkip}
      onNotInterested={handleNotInterested}
      onGenerateOTP={handleGenerateOTP}
      onVerifyOTP={handleVerifyOTP}
    />
  )
}

export default GlobalPhoneCollectionModal
